/**
 * HierarchicalScenarioReview — Step 3's view when `analysis_mode ===
 * 'hierarchical'`. Two tabs via the shared TableTabNav:
 *
 *   - Current TARA  — newly identified scenarios, full CRUD
 *   - Child TARAs   — inherited scenarios, relevance + justification
 *
 * Pure controlled — `modifications` + setters come from the Step 3
 * hook via `useHierarchicalApprove`. Mirror of step2-assets/
 * HierarchicalAssetReview.tsx.
 */
import React, { useMemo, useState } from 'react';
import TableTabNav from '../../components/TableTabNav/TableTabNav';
import ScenarioTable from './ScenarioTable';
import ChildScenariosTable, { type ChildScenarioView } from './ChildScenariosTable';
import type { ScenarioRecord } from './scenarioFieldSchema';
import type { HierarchicalModifications } from '../stepBodyProps';

const TAB_CURRENT = 'current';
const TAB_CHILD = 'child';

export interface HierarchicalScenarioReviewProps {
  newScenarios: ScenarioRecord[];
  childScenarios: ChildScenarioView[];
  editable: boolean;
  onAdd: () => void;
  onEdit: (scenario: ScenarioRecord) => void;
  onDelete: (scenario: ScenarioRecord) => void;
  /** Export + import slots — only wired into the Current TARA tab.
   *  Child TARAs tab is read-only on the data side. */
  onExport?: () => void;
  onImport?: (file: File) => void;
  modifications: HierarchicalModifications;
  onToggleRelevance: (damageId: string, isRelevant: boolean) => void;
  onJustificationChange: (damageId: string, justification: string) => void;
  childInputsDisabled?: boolean;
  /** Persist child modifications on Done (Story 15.F). */
  onSaveChildModifications?: () => Promise<void> | void;
}

export default function HierarchicalScenarioReview({
  newScenarios,
  childScenarios,
  editable,
  onAdd,
  onEdit,
  onDelete,
  onExport,
  onImport,
  modifications,
  onToggleRelevance,
  onJustificationChange,
  childInputsDisabled,
  onSaveChildModifications,
}: HierarchicalScenarioReviewProps) {
  const [activeTab, setActiveTab] = useState<string>(TAB_CURRENT);

  const childView: ChildScenarioView[] = useMemo(
    () =>
      childScenarios.map((c) => ({
        ...c,
        is_relevant: modifications[c.damage_id]?.is_relevant ?? c.is_relevant ?? true,
        relevance_justification:
          modifications[c.damage_id]?.relevance_justification ?? c.relevance_justification ?? '',
      })),
    [childScenarios, modifications],
  );

  const handleToggle = (damageId: string) => {
    const current = childView.find((c) => c.damage_id === damageId);
    onToggleRelevance(damageId, !(current?.is_relevant !== false));
  };

  const tabs = [
    { key: TAB_CURRENT, label: `Current TARA (${newScenarios.length})` },
    { key: TAB_CHILD, label: `Child TARAs (${childScenarios.length})` },
  ];

  return (
    <>
      <TableTabNav
        tabs={tabs}
        activeKey={activeTab}
        onChange={setActiveTab}
        ariaLabel="Hierarchical damage scenario review tabs"
        bleed={false}
        sticky={false}
      />

      {activeTab === TAB_CURRENT ? (
        <ScenarioTable
          scenarios={newScenarios}
          editable={editable}
          onAdd={onAdd}
          onEdit={onEdit}
          onDelete={onDelete}
          onExport={onExport}
          onImport={onImport}
          emptyMessage="No new damage scenarios yet for this TARA."
        />
      ) : (
        <ChildScenariosTable
          scenarios={childView}
          onToggleRelevance={handleToggle}
          onJustificationChange={onJustificationChange}
          disabled={childInputsDisabled}
          onSave={onSaveChildModifications}
        />
      )}
    </>
  );
}
